import React from 'react';
import PropTypes from 'prop-types';

import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import OEResizeObserver from '../../../lib/components/oe-resize-observer';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import retardUpdate from '../../../lib/react/update-retarder';

export const OEPresentationSlideMagnificationPlacement = {
    top:                        'top',
    right:                      'right',
    bottom:                     'bottom',
    left:                       'left'
};

OEPresentationSlideMagnificationPlacement.all = Object.values(OEPresentationSlideMagnificationPlacement);

export default class OEPresentationSlideMagnification extends React.PureComponent {

    constructor(props) {
        super(props);

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.unitSize = 16;

        this.presID = this.props.presID;
        this.slideID = this.getSlideID();

        this.state = {
            rect: this.getRect(),
            image: null,
            displayingImage: null,
        };

        this.onUnitSizeResize = this.onUnitSizeResize.bind(this);

        this.onSlideSet = this.onSlideSet.bind(this);
        this.onSlideAdded = this.onSlideAdded.bind(this);
        this.onSlideRemoved = this.onSlideRemoved.bind(this);
        this.onSlideMoved = this.onSlideMoved.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        let idsChanged = false;
        if(nextProps.presID !== this.props.presID || nextProps.slideID !== this.props.slideID || nextProps.firstSlide !== this.props.firstSlide)    {
            idsChanged = this.updateIDs(nextProps, false);
        }

        if(nextProps.magnification !== this.props.magnification || nextProps.placement !== this.props.placement || nextProps.offset !== this.props.offset || !OEToolbox.shallowEqual(nextProps.parentRect, this.props.parentRect) || !OEToolbox.shallowEqual(nextProps.itemRect, this.props.itemRect))  {
            this.updateRect(nextProps);
        }

        if(idsChanged || nextProps.enabled !== this.props.enabled)    {
            this.updateImage(nextProps, true);
        }
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.presentationSlideSet, this.onSlideSet);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.presentationSlideAdded, this.onSlideAdded);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.presentationSlideRemoved, this.onSlideRemoved);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.presentationSlideMoved, this.onSlideMoved);
    }

    onRelease()  {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.presentationSlideSet, this.onSlideSet);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.presentationSlideAdded, this.onSlideAdded);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.presentationSlideRemoved, this.onSlideRemoved);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.presentationSlideMoved, this.onSlideMoved);
    }

    clear() {
        this.setState({image: null, displayingImage: null});
    }

    getSlideID(props)   {
        props = props || this.props;
        if(!props.firstSlide)    return props.slideID;
        if(props.presID < 0 || !this.oe.isReady())  return -1;

        let presentation = this.oe.sharedInterface.getUIControllerPresentation();

        let presIndex = presentation.getPresentationIndexForID(props.presID);
        if(presIndex < 0)   return -1;
        return presentation.getSlideID(presIndex, 0);
    }

    updateIDs(props, updateImage = true)    {
        props = props || this.props;

        let prevPresID = this.presID;
        let prevSlideID = this.slideID;

        this.presID = props.presID;
        this.slideID = this.getSlideID(props);
    
        if(prevPresID == this.presID && prevSlideID == this.slideID)   return false;

        if(updateImage) this.updateImage(props, true);
        
        return true;
    }

    getRect(props)  {
        props = props || this.props;

        if(!props.parentRect || !props.itemRect)    return null;

        const unitScale = this.unitSize ? this.unitSize / 16 : 1;

        const scale = props.magnification;
        let offset = unitScale * props.offset;
        let width =  scale * props.itemRect.w;
        let height = scale * props.itemRect.h;

        if(props.placement === OEPresentationSlideMagnificationPlacement.right || props.placement === OEPresentationSlideMagnificationPlacement.left)   {
            height = Math.min(height, props.parentRect.h);
        } else {
            width = Math.min(width, props.parentRect.w);
        }

        let x, y;

        if(props.placement === OEPresentationSlideMagnificationPlacement.bottom)    {
            x = Math.min(Math.max(props.itemRect.x + 0.5 * props.itemRect.w - 0.5 * width - props.parentRect.x, 0), props.parentRect.w - width);
            y = props.itemRect.y - props.parentRect.y + props.itemRect.h + offset;
        } else if(props.placement === OEPresentationSlideMagnificationPlacement.right) {
            x = props.itemRect.x - props.parentRect.x + props.itemRect.w + offset;
            y = Math.min(Math.max(props.itemRect.y + 0.5 * props.itemRect.h - 0.5 * height - props.parentRect.y, 0), props.parentRect.h - height);
        } else if(props.placement === OEPresentationSlideMagnificationPlacement.left) {
            x = props.itemRect.x - props.parentRect.x - (width + offset);
            y = Math.min(Math.max(props.itemRect.y + 0.5 * props.itemRect.h - 0.5 * height - props.parentRect.y, 0), props.parentRect.h - height);
        } else {    // top
            x = Math.min(Math.max(props.itemRect.x + 0.5 * props.itemRect.w - 0.5 * width - props.parentRect.x, 0), props.parentRect.w - width);
            y = props.itemRect.y - props.parentRect.y - (height + offset);
        }

        return {x: x, y: y, w: width, h: height};
    }

    updateRect(props) {
        this.setState({rect: this.getRect(props)});
    }

    updateImage(props, clear) {
        props = props || this.props;

        if(!props.enabled)  {
            this.clear();
            return;
        }

        if(clear)   this.setState({image: null});

        this.oe.sharedInterface.getUIControllerPresentation().getThumbnailID(this.presID, this.slideID, (thumb, presID, slideID) => {
            if(this.presID !== presID || this.slideID !== slideID || !props.enabled)   return;
            if(!thumb)   {
                this.setState({image: null});
                return;
            }
            this.setState({image: thumb, displayingImage: thumb});
        });
    }

    updateState(released)   {
        if(!this.oe.isReady() || released === true)   {
            this.clear();
            return;
        }

        retardUpdate(this, () => {
            this.updateIDs(undefined, false);
            this.updateImage();
        });
    }

    onSlideSet(message, userInfo)   {
        if(this.presID !== userInfo.presID || this.slideID !== userInfo.slideID) return;
        this.updateImage();
    }

    onSlideAdded(message, userInfo)  {
        if(this.presID !== userInfo.presID) return;

        if(this.slideID === userInfo.slideID) {
            this.updateImage();
            return;
        }

        if(this.props.firstSlide && userInfo.slide === 0)   {
            this.updateIDs();
            return;
        }
    }

    onSlideRemoved(message, userInfo)    {
        if(this.presID !== userInfo.presID)   return;

        if(this.props.firstSlide && userInfo.slide === 0)   {
            this.updateIDs();
            return;
        }
    }

    onSlideMoved(message, userInfo)    {
        if(this.presID === userInfo.presSrcID)  {
            if(this.props.firstSlide && userInfo.slideSrc === 0)   {
                this.updateIDs();
                return;
            }
        }

        if(this.presID === userInfo.presDstID)  {
            if(this.props.firstSlide && userInfo.slideDst === 0)   {
                this.updateIDs();
                return;
            }
        }
    }

    onUnitSizeResize(sender, size)   {
        if(this.unitSize === size.w) return;
        this.unitSize = size.w;
        this.updateRect();
    }

    render() {
        if(!this.state.rect || !this.state.displayingImage) return null;

        let visible = this.props.visible && this.state.image;
        
        return (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                <div 
                    className={'presentation-slide-magnification' + (!visible ? ' hide ' : ' ') + this.props.className}
                    style={{
                        backgroundImage: 'url(' + this.state.displayingImage + ')',
                        left: this.state.rect.x,
                        top: this.state.rect.y,
                        width: this.state.rect.w,
                        height: this.state.rect.h,
                    }}
                >
                    <OEResizeObserver className="unit-size-observer" onResize={this.onUnitSizeResize}/>
                </div>
            </React.Fragment>
        );
    }
}

OEPresentationSlideMagnification.defaultProps = {
    className: '',
    moduleId: '',
    enabled: true,
    visible: true,
    magnification: 1,
    placement: OEPresentationSlideMagnificationPlacement.top,
    offset: 12,
    presID: -1,
    slideID: -1,
    firstSlide: false
};

OEPresentationSlideMagnification.propTypes = {
    className: PropTypes.string,
    moduleId: PropTypes.string,
    enabled: PropTypes.bool,
    visible: PropTypes.bool,
    magnification: PropTypes.number,
    placement: PropTypes.oneOf(OEPresentationSlideMagnificationPlacement.all),
    offset: PropTypes.number,
    presID: PropTypes.number,
    slideID: PropTypes.number,
    firstSlide: PropTypes.bool,
    parentRect: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        w: PropTypes.number.isRequired,
        h: PropTypes.number.isRequired,
    }),
    itemRect: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        w: PropTypes.number.isRequired,
        h: PropTypes.number.isRequired,
    })
};